import React from 'react';
import { Crown, AlertTriangle, CheckCircle } from 'lucide-react';
import { useUsage } from '../../contexts/UsageContext';
import { publicPlanLabel, getUsageDisplayValues } from '../../utils/planLabels';

const SubscriptionNotification = ({ className = "" }) => {
  const { usage, subscription } = useUsage();

  if (!usage) {
    return null;
  }

  const { used: totalUsed, limit, periodLabel, isDaily } = getUsageDisplayValues(usage);
  const remaining = Math.max(0, limit - totalUsed);
  const isExhausted = remaining === 0 && limit > 0; // Only exhausted if limit > 0 and remaining is 0
  const isNearLimit = remaining <= 1 && remaining > 0;
  const usagePercentage = limit > 0 ? (totalUsed / limit) * 100 : 0;

  const getStatusColor = () => {
    // Always use the same light cream background color
    return 'bg-[#FEF9C3] text-gray-800';
  };

  const getStatusIcon = () => {
    if (isExhausted) return <AlertTriangle className="w-4 h-4 text-gray-700" />;
    if (isNearLimit) return <AlertTriangle className="w-4 h-4 text-gray-700" />;
    return <CheckCircle className="w-4 h-4 text-gray-700" />;
  };

  const planLabel = publicPlanLabel(usage.subscription_plan || 'free');

  const getStatusMessage = () => {
    if (isExhausted) {
      return (
        <span>
          {isDaily
            ? `You have reached your daily ${planLabel} limit (${totalUsed}/${limit}). Resets tomorrow. `
            : `You have reached your ${planLabel} limit (${totalUsed}/${limit}). `}
          <button 
            onClick={() => window.location.href = '/subscriptions?tab=plans'}
            className="text-blue-600 hover:text-blue-700 underline font-medium cursor-pointer"
          >
            Upgrade Plan
          </button>
          {' '}to continue.
        </span>
      );
    }
    if (isNearLimit) {
      return `You have ${remaining} usage remaining on your ${planLabel} (${totalUsed}/${limit} ${periodLabel}). Consider upgrading soon.`;
    }
    return `You have ${remaining} usage remaining on your ${planLabel} (${totalUsed}/${limit} ${periodLabel}).`;
  };

  return (
    <div className={`${getStatusColor()} rounded-lg p-4 ${className}`}>
      <div className="flex items-start space-x-3">
        <Crown className="w-5 h-5 text-gray-700 flex-shrink-0" />
        <div className="flex-1">
          <h3 className="font-semibold text-sm text-gray-800">
            {isExhausted ? 'Subscription Limit Reached' : 'Subscription Status'}
          </h3>
          <p className="text-xs text-gray-700">
            {getStatusMessage()}
          </p>
        </div>
        <div className="flex items-center space-x-2 flex-shrink-0">
          {getStatusIcon()}
          <span className="text-sm font-medium text-gray-800">
            {totalUsed}/{limit}
          </span>
        </div>
      </div>
      
      {/* Progress bar - only show for non-exhausted states */}
      {!isExhausted && (
        <div className="mt-3">
          <div className="w-full bg-gray-300 rounded-full h-2">
            <div 
              className="bg-gray-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.min(usagePercentage, 100)}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionNotification;
